import React from "react"
import { useTranslation } from "react-i18next"
import { Bar } from "react-chartjs-2"
import { round } from "services/units/round"
import useTranslatedLabels from "services/charts/translatedLabels"
import { BOTTOM_LEGEND, HIDDEN_LEGEND } from "services/charts/legend"
import { dataLabelsPlugin } from "services/charts/plugins/datalabeles"
import { getColorByPillar, getCompareBarColors } from "services/charts/ColorBar"
import { yAxes } from "services/charts/yAxes"
import { DEFAULT_OPTIONS } from "services/charts/default"
import { ChartContainer } from "views/organisms/organization/tables/ChartContainer"
import PropTypes from "prop-types"
import { convertValuesToReadable } from "services/units/valueConversion"
import { DETERMINE_UNITS } from "services/units/units"
import { cutLabel } from "views/atoms/text/cutLabel"

const BarChartTemplate = ({
  title,
  data,
  compareBarColors,
  modifiedBarColors,
  dataBarTitle,
  labelPath,
  labelKeys,
  unitPath,
  unitKey,
  pillar,
  showLegend,
  helpText,
  cutLabels,
}) => {
  const { t } = useTranslation()
  const translatedLabels = useTranslatedLabels(labelPath, labelKeys)

  let translatedLabelsWithCut = translatedLabels

  if (cutLabels && typeof cutLabels === "object") {
    const { maxLength, visibleChars } = cutLabels
    translatedLabelsWithCut = cutLabel(
      translatedLabels,
      maxLength,
      visibleChars
    )
  }

  const convertedValue = convertValuesToReadable(
    data,
    unitKey,
    DETERMINE_UNITS(unitKey)
  )
  const roundedData = convertedValue.values.map((value) => round(value))
  const backgroundColor = compareBarColors
    ? getCompareBarColors(data)
    : modifiedBarColors
    ? modifiedBarColors
    : getColorByPillar(pillar)

  const translatedUnit = t(`${unitPath}.${convertedValue.unit}`)

  const datasets = {
    labels: cutLabels ? translatedLabelsWithCut : translatedLabels,
    datasets: [
      {
        label: dataBarTitle || "",
        data: roundedData,
        backgroundColor: backgroundColor,
        unit: translatedUnit,
        ...(cutLabels && { originalLabels: translatedLabels }),
      },
    ],
  }

  const options = {
    ...DEFAULT_OPTIONS,
    legend: HIDDEN_LEGEND,
    ...yAxes(translatedUnit),
    plugins: dataLabelsPlugin(undefined, undefined),
    tooltips: {
      ...DEFAULT_OPTIONS.tooltips,
      callbacks: {
        ...DEFAULT_OPTIONS.tooltips.callbacks,
        title: (tooltipItem) => {
          const index = tooltipItem[0].index
          return translatedLabels[index]
        },
      },
    },
  }

  if (showLegend) {
    options.legend = BOTTOM_LEGEND
  }

  return (
    <ChartContainer title={title} helpText={helpText} pillar={pillar}>
      <Bar data={datasets} options={options} />
    </ChartContainer>
  )
}

export default BarChartTemplate

BarChartTemplate.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.array.isRequired,
  compareBarColors: PropTypes.bool,
  modifiedBarColors: PropTypes.array,
  dataBarTitle: PropTypes.string,
  labelPath: PropTypes.string,
  labelKeys: PropTypes.array.isRequired,
  unitPath: PropTypes.string.isRequired,
  unitKey: PropTypes.string.isRequired,
  pillar: PropTypes.string,
  helpText: PropTypes.string.isRequired,
  showLegend: PropTypes.bool,
  cutLabels: PropTypes.shape({
    maxLength: PropTypes.number,
    visibleChars: PropTypes.number,
  }),
}

BarChartTemplate.defaultProps = {
  pillar: "environmental",
  compareBarColors: false,
  modifiedBarColors: null,
  dataBarTitle: "",
  showLegend: false,
  cutLabels: null,
  maxLength: 10,
  visibleChars: 10,
}
